import { Indicator } from '../proto/generated/indicator'
import {
    PATTERNS_GROUP_NAME,
    PATTERN_SUBCATEGORY_CANDLESTICK,
    PATTERN_SUBCATEGORY_CHART,
} from '../assets/patternSubcategories'
import { SeriesOutput } from '../components/chartV2/chart.typings'

export function normalizeIndicatorCode(code?: string): string {
    return (code ?? '').replace(/\W/g, '_').toUpperCase()
}

export function hasChartPatternOverlayOutput(indicator?: Indicator): boolean {
    return Boolean(
        indicator?.outputs?.some((output) => output.drawingType === 'chartPatternOverlay'),
    )
}

export function isPatternIndicator(indicator?: Indicator): boolean {
    return indicator?.groupName === PATTERNS_GROUP_NAME && hasChartPatternOverlayOutput(indicator)
}

export function findPatternIndicator(code: string, indicators: Indicator[]): Indicator | undefined {
    const normalized = normalizeIndicatorCode(code)
    return indicators.find(
        (ind) => normalizeIndicatorCode(ind.code) === normalized && isPatternIndicator(ind),
    )
}

export function isPatternIndicatorCode(code: string, indicators: Indicator[]): boolean {
    return Boolean(findPatternIndicator(code, indicators))
}

export function isChartPatternIndicator(indicator?: Indicator): boolean {
    if (!isPatternIndicator(indicator)) return false
    const description = indicator!.description?.trim()
    if (description === PATTERN_SUBCATEGORY_CHART) return true
    if (description === PATTERN_SUBCATEGORY_CANDLESTICK) return false
    const inputCodes = new Set(indicator!.inputs.map((input) => input.code))
    return inputCodes.has('PATTERNS') || inputCodes.has('PRICE_TARGETS') || inputCodes.has('TREND_HEIGHT')
}

export function isCandlestickPatternIndicator(indicator?: Indicator): boolean {
    return isPatternIndicator(indicator) && !isChartPatternIndicator(indicator)
}

export function isChartPatternIndicatorCode(code: string, indicators: Indicator[]): boolean {
    return isChartPatternIndicator(findPatternIndicator(code, indicators))
}

export function isCandlestickPatternIndicatorCode(code: string, indicators: Indicator[]): boolean {
    return isCandlestickPatternIndicator(findPatternIndicator(code, indicators))
}

export function getCandlestickPatternBias(indicator?: Indicator, code?: string): 'bullish' | 'bearish' | undefined {
    const normalized = normalizeIndicatorCode(code ?? indicator?.code)
    const name = `${indicator?.name ?? ''} ${indicator?.shortName ?? ''}`
    if (normalized.startsWith('BEARISH_') || /\bbear/i.test(name)) return 'bearish'
    if (indicator && isCandlestickPatternIndicator(indicator)) return 'bullish'
    if (code && /\bbear/i.test(normalized)) return 'bearish'
    return indicator || code ? 'bullish' : undefined
}

function extractShowPriceTarget(settings?: Record<string, unknown>): boolean | undefined {
    if (!settings) return undefined
    const nested = (settings as { settings?: Record<string, unknown> }).settings ?? settings
    if ('showPriceTarget' in nested) return Boolean(nested.showPriceTarget)
    return undefined
}

export function patternSupportsPriceTarget(
    indicator?: Indicator,
    drawingSettings?: Record<string, unknown>,
): boolean {
    const fromOutputSettings = extractShowPriceTarget(drawingSettings)
    if (fromOutputSettings != null) return fromOutputSettings
    if (!indicator) return false
    const output = indicator.outputs.find((item) => item.drawingType === 'chartPatternOverlay')
    return extractShowPriceTarget(output?.drawingSettings as Record<string, unknown> | undefined) ?? false
}

export function buildPatternDefaultOutputValues(
    seriesIds: string[],
    outputValues: Record<string, SeriesOutput>,
    indicator?: Indicator,
): Record<string, SeriesOutput> {
    const seriesId = seriesIds[0]
    const current = seriesId ? outputValues[seriesId] : undefined
    if (!seriesId || !current || !indicator) return { ...outputValues }

    const indicatorOutput = indicator.outputs.find((item) => item.drawingType === 'chartPatternOverlay')
    if (!indicatorOutput) return { ...outputValues }

    return {
        ...outputValues,
        [seriesId]: {
            ...current,
            drawingSettings: indicatorOutput.drawingSettings,
            colors: indicatorOutput.colors ?? current.colors,
        },
    }
}

export function usesStructuredWedgePatternLayout(drawingSettings?: Record<string, unknown>): boolean {
    const raw = (drawingSettings as { settings?: Record<string, unknown> })?.settings ?? drawingSettings ?? {}
    return 'showPatternLines' in raw || 'showTrendlines' in raw
}

export function usesTrianglePatternLayout(drawingSettings?: Record<string, unknown>): boolean {
    const raw = (drawingSettings as { settings?: Record<string, unknown> })?.settings ?? drawingSettings ?? {}
    return /triangle/i.test(String(raw.patternTitle ?? ''))
}
